import {jobRequiresActionPlan} from '~/helpers/allmaJobsHelpers'
import {CUSTOMER_TYPES_SHAREHOLDER} from '~/constants/customers'
import {ADDITIONS} from '~/constants/contractAdditions'
import {
    CONTRACT_TYPES_USING_ENVIRONMENT_REPORT_V2,
    CONTRACT_TYPES_WITH_YOUNG_FOREST_CARE_QUESTION_SECTION,
    SW_ENVIRONMENTS
} from '~/constants/environments'
import {
    CONTRACT_FIELD_COLLECTIONPOINT_LENGT_MIN,
    CONTRACT_SIGNATURE_TYPE_CODE_TABLET,
    CONTRACT_TYPE_REGISTRATION,
    CONTRACT_TYPE_SILVICULTURE_AGREEMENT,
    CONTRACT_TYPE_SILVICULTURE_ORDER, CONTRACT_TYPE_STEPS,
    CONTRACT_TYPE_SUPPLY_WOOD,
} from '~/constants/contracts'
import type {AllmaAction, AllmaJob} from '~/models/allmajob'
import type {ActionPlan, FullContract} from '~/models/contract'
import type {ComputedRef} from "vue"
import {
    ALLMA_JOB_TYPE_ADDITIVE_PLANTING,
    ALLMA_JOB_TYPE_DITCH_CLEARING,
    ALLMA_JOB_TYPE_HOGGER,
    ALLMA_JOB_TYPE_PLANTING,
    ALLMA_JOB_TYPE_THINNING,
    ALLMA_JOB_TYPE_YOUNG_TREE_CARE,
    ALLMA_JOB_TYPES,
    type AllmaJobType
} from "~/constants/allmaJobs";
import {
    QUESTION_ENABLE_COMMENT_ALWAYS_OPTIONAL,
    QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED,
    QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED_IF_NO,
    QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED_IF_YES,
    QUESTION_ENABLE_COMMENT_NEVER,
    QUESTION_OPTION_CONFIG_TEXT,
    QUESTION_TEMPLATE_TYPE_ENVIRONMENT_CONSIDERATION
} from "~/constants/questions";
import {featureEnabled} from "~/helpers/features";
import {
    GmDtoLibraryDtoActionModelsLibraryPowerOfAttorneyComLib, GmDtoLibraryDtoProductionOrderDto,
    GMServicesCommonQuestionsQuestionDefinition,
    GMServicesCommonQuestionsQuestionTemplate
} from "~/gen/openapi/sblService";
import {contractIsExcludedFromForestFund} from "~/helpers/contracts";
import {Customer} from "~/models/customers";
import {useQuestionService} from "~/services/questionService";
import {useProductService} from "~/services/productService";
import {ProductionTypeFellingAndDelivery} from "~/constants/productionOrders";

export const isPageValid = (
    page: ComputedRef<string>,
    contract: ComputedRef<FullContract>,
    currentPropertyPowerOfAttorneys: Ref<Array<GmDtoLibraryDtoActionModelsLibraryPowerOfAttorneyComLib> | undefined>,
    isLoadingPowerOfAttorneys: Ref<boolean>,
    customer?: Customer,
    isLoadingAllmaJobs?: Ref<boolean>,
    questionTemplates?: Ref<Array<GMServicesCommonQuestionsQuestionTemplate | undefined>>,
): ComputedRef<boolean> => {
  const runtimeConfig = useRuntimeConfig()

  return computed(() => {
    switch (page.value) {
      case 'loggingperiod':
        if (!(isNaN(Number(contract.value.InternalNo)) || Number(contract.value.InternalNo) <= 999)) {
          return false
        }
        if (contract.value.Type === CONTRACT_TYPE_SILVICULTURE_AGREEMENT) {
          return contract.value.SilvicultureYear && contract.value.SilvicultureYear > 0 && contract.value.SilviculturePeriod && contract.value.SilviculturePeriod > 0
        }
        return contract.value.Year && contract.value.Year > 0 && contract.value.Period && contract.value.Period > 0 && contract.value.LoggingMonth && contract.value.LoggingMonth > 0
      case 'actionplans':
          if (contract.value.Type !== CONTRACT_TYPE_SILVICULTURE_ORDER && !contract.value.AllmaJobs?.length) {
              return false
          }
        return isActionPlansValid(contract.value, !!isLoadingAllmaJobs?.value)
      case 'location':
        return contract.value.Location && contract.value.Location.length > 2
      case 'environmentreport':
        if (contract.value.Type && CONTRACT_TYPES_USING_ENVIRONMENT_REPORT_V2.includes(contract.value.Type)) {
          return !!questionTemplates && isEnvironmentReportValid(contract.value, false, questionTemplates)
        }
        return (
          contract.value.Type !== CONTRACT_TYPE_SUPPLY_WOOD ||
          (!!contract.value.Environment.EnvironmentResponsibleName)
        )
      case 'volumeAndEntrepreneur':
        return (
            customer?.IsLargeForestOwner && contract.value.Type === CONTRACT_TYPE_SUPPLY_WOOD)
            || (
                (contract.value.AssortmentInformation?.TotalWoodForSale && contract.value.AssortmentInformation.TotalWoodForSale > 0)
                && (contract.value.Type === CONTRACT_TYPE_REGISTRATION || contract.value.Type === CONTRACT_TYPE_SUPPLY_WOOD || contract.value.AssortmentInformation.EntrepreneurCapacity > 0)
            )
      case 'additions':
        return isAdditionsValid(contract.value)
      case 'assortments':
        return contract.value.Details?.filter(d => !d.Deleted).length > 0 && contract.value.Details?.filter(d => !d.Deleted && d.Quantity < 1).length < 1
      case 'environmentcheck':
        return isEnvironmentCheckValid(contract.value)
      case 'prices':
        return true
      case 'toppleplaces':
        return true
      case 'agreement':
        return contract.value.Type === CONTRACT_TYPE_REGISTRATION || contract.value.Type === CONTRACT_TYPE_SUPPLY_WOOD || isAgreementValid(contract.value)
      case 'silviculture':
        const excludedFromYoungForestCareQuestions = computed(() => contract.value.Type === CONTRACT_TYPE_SILVICULTURE_ORDER)
        return excludedFromYoungForestCareQuestions.value ? true : contract.value.Sivilculture?.NeedYoungForestCareAssessedOnProperty !== undefined &&
            contract.value.Sivilculture?.NeedYoungForestCare !== undefined &&
            (contract.value.Sivilculture?.NeedYoungForestCare ? !!contract.value.Sivilculture?.PlanImplementationOfYoungForestCare : true)
      case 'silvicultureSummary':
        return true
      case 'finaldetails':
        return (customer?.CustomerType === CUSTOMER_TYPES_SHAREHOLDER || typeof contract.value?.Completition?.OrderOfShareOwnership !== 'undefined') &&
          ([CONTRACT_TYPE_SILVICULTURE_ORDER, CONTRACT_TYPE_SILVICULTURE_AGREEMENT].includes(contract.value.Type) || contractIsExcludedFromForestFund(contract.value) || (contract.value.ForestFundRate >= 4 && contract.value.ForestFundRate <= 40)) &&
          ([CONTRACT_TYPE_SILVICULTURE_ORDER, CONTRACT_TYPE_SILVICULTURE_AGREEMENT].includes(contract.value.Type) || (!!contract.value.CollectionPoint && contract.value.CollectionPoint !== "" && contract.value.CollectionPoint.length >= CONTRACT_FIELD_COLLECTIONPOINT_LENGT_MIN))
      case 'summary':
        return true
      case 'details':
        const fixedPriceActions = contract.value.AllmaJobs
            ?.flatMap((job) => job.Actions || [])
            .filter((action) => action.IsFixedPrice);
        if (fixedPriceActions?.length && fixedPriceActions?.length > 0) {
          return fixedPriceActions.every((action) => {
            return !!action.FixedPrice && action.Type?.toUpperCase()?.includes('PLANTING') ? !!action.PlantSaleFixedPrice : !!action.FixedPrice
          })
        }
        return true
      case 'sign':
        if (!contract.value.SignatureTypeCode || isLoadingPowerOfAttorneys.value) {
          return false
        }

        if (featureEnabled(runtimeConfig.public.FEATURE_SIGN_WITH_BANKID) && currentPropertyPowerOfAttorneys.value && currentPropertyPowerOfAttorneys.value.length > 0) {
          return !!contract.value.BankIdSigningContactId
        }

        if (contract.value.SignatureTypeCode === CONTRACT_SIGNATURE_TYPE_CODE_TABLET) {
          return !!contract.value.SignatureBuyer
        }

        return false
      default:
        return false
    }
  })
}

// Validation logic for environmentConsideration
export const isEnvironmentReportValid = (contract: FullContract, checkReadyForSign: Boolean, questionTemplates: Ref<Array<GMServicesCommonQuestionsQuestionTemplate | undefined>>): boolean => {
  const { getQuestionTemplateByType } = useQuestionService()
  const questionTemplate = computed(() => !!questionTemplates.value?.length ? getQuestionTemplateByType(questionTemplates.value, QUESTION_TEMPLATE_TYPE_ENVIRONMENT_CONSIDERATION, contract): {})

  if (!questionTemplate.value?.QuestionDefinitions?.length) {
    return true
  }

  const isRequiredYoungForestCareQuestionsAndCommentsAnswered = computed(() => {
    if (CONTRACT_TYPES_WITH_YOUNG_FOREST_CARE_QUESTION_SECTION.includes(contract.Type!)) {
      return contract.Sivilculture?.NeedYoungForestCareAssessedOnProperty !== undefined &&
          contract.Sivilculture?.NeedYoungForestCare !== undefined &&
          (contract.Sivilculture?.NeedYoungForestCare ? !!contract.Sivilculture?.PlanImplementationOfYoungForestCare : true)
    }
    return true
  })

  const isAllRequiredQuestionsAndCommentsAnswered = isRequiredYoungForestCareQuestionsAndCommentsAnswered.value && questionTemplate.value?.QuestionDefinitions?.every((questionDefinition: GMServicesCommonQuestionsQuestionDefinition) => {
    const questionResponse = contract.EnvironmentConsideration?.QuestionResponses?.find((response) => response.QuestionDefinitionId === questionDefinition.Id)

    const minimumCommentLengthOk = () => {
      const commentMinChars = questionDefinition.CommentMinChars
      const commentLength = questionResponse?.Comment?.length || 0
      if (commentMinChars) {
        return commentLength >= commentMinChars
      }
      return true
    }

    const commentIsValid = () => {
      const OPTION_YES = 'True'
      const OPTION_NO = 'False'

      switch (questionDefinition.EnableComment) {
          // @ts-ignore
        case QUESTION_ENABLE_COMMENT_NEVER:
          return true
          // @ts-ignore
        case QUESTION_ENABLE_COMMENT_ALWAYS_OPTIONAL:
          return true
          // @ts-ignore
        case QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED_IF_YES:
          return questionResponse?.Value === OPTION_YES ? minimumCommentLengthOk() : true
          // @ts-ignore
        case QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED_IF_NO:
          return questionResponse?.Value === OPTION_NO ? minimumCommentLengthOk() : true
          // @ts-ignore
        case QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED:
          return minimumCommentLengthOk()
        default:
          return true
      }
    }

    // text questions only check comment
    if (questionDefinition.OptionConfig === QUESTION_OPTION_CONFIG_TEXT) {
      if (questionDefinition.Required) {
        return !!questionResponse?.Comment && minimumCommentLengthOk()
      }
      return true
    } else if (checkReadyForSign) {
      if (questionResponse?.Value === undefined || !questionResponse?.Value?.toString()?.length) {
        return false
      }
    }

    if (questionDefinition.Required) {
      return commentIsValid() && !!questionResponse?.Value
    }

    return commentIsValid()
  })

  return !!isAllRequiredQuestionsAndCommentsAnswered
}

// Contract must have active actionplans or active and valid allma jobs
const isActionPlansValid = (contract: FullContract, isLoadingAllmaJobs: boolean): boolean => {
  return !!(
      (contract.ActionPlans && contract.ActionPlans.filter((actionPlan: ActionPlan) => !actionPlan.Deleted).length > 0) ||
      (contract.AllmaJobs && contract.AllmaJobs.filter((job: AllmaJob) => !job.Removed).length > 0 && allAllmaJobsAreValid(contract.AllmaJobs) && !isLoadingAllmaJobs)
  )
}

// All additions executionfield and payfield must be set correctly. Not relevant on both, or value on both.
const isAdditionsValid = (contract: FullContract): boolean => {
  return contract.AgreementDetails && ADDITIONS.filter(addition =>
    (contract.AgreementDetails[addition.ExecuteField] === null || contract.AgreementDetails[addition.PayField] === null) ||
    (typeof contract.AgreementDetails[addition.ExecuteField] === 'undefined' || typeof contract.AgreementDetails[addition.PayField] === 'undefined') ||
    (contract.AgreementDetails[addition.ExecuteField] === 0 && contract.AgreementDetails[addition.PayField] !== 0) ||
    (contract.AgreementDetails[addition.ExecuteField] !== 0 && contract.AgreementDetails[addition.PayField] === 0)).length === 0
}

// On supply wood, all environments must be set to true
const isEnvironmentCheckValid = (contract: FullContract): boolean => {
  return contract.Environment && SW_ENVIRONMENTS.filter(environment =>
    typeof contract.Environment[environment.Field] === 'undefined' || contract.Environment[environment.Field] === false).length === 0
}

const allmaActionCommonFieldsAreValid = (allmaAction: AllmaAction): boolean => {
  return !!(
      allmaAction.Plot?.length &&                                 // Teig
      allmaAction.Stand?.length &&                                // Bestand
      allmaAction.Wood && allmaAction.Wood > 0 &&                 // Treslag
      allmaAction.SiteQuality && allmaAction.SiteQuality > 0 &&   // Bonitet
      allmaAction.Masl && allmaAction.Masl > 0                    // Høydelag
  )
}

const allmaActionPlantingFieldsAreValidIfNeeded = (allmaAction: AllmaAction, jobType: AllmaJobType): boolean => {
  const { products } = useProductService()

  if (!allmaAction.Type) {
    console.error('Validation error: Allma action missing type', allmaAction)
    return false
  }

  if (jobType.TypeId === ALLMA_JOB_TYPE_ADDITIVE_PLANTING) {
    if (!allmaAction.SupplementingUpTo) return false
  }

  // There is no proper ID for the allma action type available here :/
  const actionTypeRequiresProduct = ['planting', 'suppleringsplanting'].includes(allmaAction.Type.toLowerCase())
  const jobTypeRequiresProduct = [ALLMA_JOB_TYPE_ADDITIVE_PLANTING].includes(jobType.TypeId)
  if ((actionTypeRequiresProduct || jobTypeRequiresProduct) && !products.value?.find((p) => p.Id === allmaAction.PlantSaleProductId)) {
    return false
  }
  const hasPlantingInActionPlan = !!allmaAction?.ActionPlan?.Planting
  if (hasPlantingInActionPlan) {
    // Require product if ActionPlan has Planting
    if (!products.value?.find((p) => p.Id === allmaAction?.ActionPlan?.ProductId)) return false
  }

  const needsDensity = allmaAction?.ActionPlan?.Planting || [ALLMA_JOB_TYPE_YOUNG_TREE_CARE, ALLMA_JOB_TYPE_PLANTING, ALLMA_JOB_TYPE_ADDITIVE_PLANTING].includes(jobType.TypeId)
  if (needsDensity && !allmaAction.ActionPlan) {
    if (!allmaAction.PlantsPerAcre) return false
  }

  if (needsDensity && hasPlantingInActionPlan){
    if (!allmaAction.ActionPlan?.PlantsPerAcre) return false
  }

  if (jobType.TypeId === ALLMA_JOB_TYPE_ADDITIVE_PLANTING) {
    if (!allmaAction.SupplementingUpTo) return false
  }

  if (allmaAction.Type === 'suppleringsplanting') {
    if (!allmaAction.ActionPlan?.SupplementingUpTo) return false
  }

  if (allmaAction.Type?.toUpperCase()?.includes('TRESLAGSBLANDING')) {
    if (!allmaAction.WoodMixPlantsPerAcre) return false
    if (!allmaAction.WoodMixPlantSaleProductId) return false
    if (!allmaAction.WoodMixRatio || allmaAction.WoodMixRatio < 0 || allmaAction.WoodMixRatio > 1) return false
  }

  return true
}

const allmaActionVolumeFieldIsValidIfNeeded = (allmaAction: AllmaAction, jobType: AllmaJobType): boolean => {
  const needsVolume = [ALLMA_JOB_TYPE_HOGGER, ALLMA_JOB_TYPE_THINNING].includes(jobType.TypeId)
  return needsVolume ? !!allmaAction.TotalVolume : true
}

const allmaActionMetersFieldIsValidIfNeeded = (allmaAction: AllmaAction, jobType: AllmaJobType): boolean => {
  const needsMeters = [ALLMA_JOB_TYPE_DITCH_CLEARING].includes(jobType.TypeId)
  const needsMetersInActionPlan = allmaAction.ActionPlan?.DitchClearing
  if (needsMetersInActionPlan) {
    if (!allmaAction.ActionPlan?.Meters) return false
  }
  return needsMeters ? !!allmaAction.Meters : true
}

/**
 * New validator function for allmaAction to use the same logic for validating both in modal and list
 * @param allmaAction - AllmaAction to validate
 * @param allmaJob - AllmaJob containing the AllmaAction
 */
export const allmaActionIsValid = (allmaAction: AllmaAction, allmaJob: AllmaJob): boolean => {
  const jobType = ALLMA_JOB_TYPES.find((t) => allmaJob.JobType === t.TypeId)
  if (!allmaAction.Type || !allmaAction.Type.length || !jobType) {
    console.error('Validation error: missing allma action type or allma job type', allmaAction, allmaJob)
    return false
  }

  return (
      allmaActionCommonFieldsAreValid(allmaAction) &&
      allmaActionVolumeFieldIsValidIfNeeded(allmaAction, jobType) &&
      allmaActionMetersFieldIsValidIfNeeded(allmaAction, jobType) &&
      allmaActionPlantingFieldsAreValidIfNeeded(allmaAction, jobType) &&
      (jobRequiresActionPlan(allmaJob) ? !!allmaAction.ActionPlan : true)
  )
}

/**
 * New validator function for allmajobs to use the same logic for validating both in modal and list
 * @param allmaJob - AllmaJob to validate
 */
export const allmaJobIsValid = (allmaJob: AllmaJob): boolean => {
  return allmaJob.Actions?.length ? allmaJob.Actions.every((a) => allmaActionIsValid(a, allmaJob)) : true
}

const allAllmaJobsAreValid = (allmaJobs: AllmaJob[]): boolean => {
  return allmaJobs.every((a) => allmaJobIsValid(a))
}

const isAgreementValid = (contract: FullContract): boolean => {
  return typeof contract.AgreementDetails.OperationCostType !== 'undefined' && typeof contract.AssortmentInformation.CompleteOperationTarget !== 'undefined' &&
    (
      (contract.AgreementDetails.OperationCostType === 1 && ((typeof contract.AgreementDetails.OperationPrice !== 'undefined' && contract.AgreementDetails.OperationPrice > 0) || !!contract.AgreementDetails.OperationCostNote)) ||
      (contract.AgreementDetails.OperationCostType === 2 && ((typeof contract.AgreementDetails.OperationPrice !== 'undefined' && contract.AgreementDetails.OperationPrice > 0) || !!contract.AgreementDetails.OperationCostNote)) ||
      (contract.AgreementDetails.OperationCostType === 3 && ((typeof contract.AgreementDetails.HourRateLogger !== 'undefined' && typeof contract.AgreementDetails.HourRateCarrier !== 'undefined' && contract.AgreementDetails.HourRateLogger > 0 && contract.AgreementDetails.HourRateCarrier > 0) || !!contract.AgreementDetails.OperationCostNote))
    )
}

export const hasDuplicateProductionTypes = (productionOrders: GmDtoLibraryDtoProductionOrderDto[] | undefined | null) => {
    if (productionOrders && productionOrders?.length >= 2) {
        if (productionOrders.some((po) => po.ProductionTypeCode === ProductionTypeFellingAndDelivery)) {
            return true
        }

        const types = productionOrders?.map((po) => po.ProductionTypeCode)
        return new Set(types).size !== types?.length
    }
    return false
}

export const contractTypeShouldHaveEnvironmentReport = (contractType: number) => {
    return CONTRACT_TYPE_STEPS.filter((s) => s.Steps.some((step) => step.Page === 'environmentreport')).map((s) => s.Type).includes(contractType)
}